import React from 'react';
import { Modal, Typography, Card, Tag, Space, Row, Col, Timeline, Divider, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import { 
  VideoCameraOutlined, 
  GlobalOutlined, 
  HomeOutlined, 
  QuestionOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  BookOutlined,
  EnvironmentOutlined,
  HistoryOutlined,
  EditOutlined,
  SwapOutlined
} from '@ant-design/icons';
import moment from 'moment-timezone';

const { Title, Text } = Typography;

const ClassDetailsModal = ({ visible, onClose, classData, teacherData, classChangeLogs }) => {
  const { t } = useTranslation();

  // Get teacher name
  const getTeacherName = () => {
    if (!teacherData) return '-';
    return `${teacherData.nickname || ''} ${teacherData.firstname || ''} ${teacherData.lastname || ''}`.trim();
  };

  // Get status tag
  const getStatusTag = (status) => {
    if (!status) return <Tag color="default">No Status</Tag>;
    
    switch(status.toLowerCase()) {
      case 'approved':
        return <Tag color="green">Approved</Tag>;
      case 'pending':
        return <Tag color="orange">Pending</Tag>;
      case 'cancelled':
        return <Tag color="red">Cancelled</Tag>;
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };

  const formatPlatform = (platform) => {
    switch (platform) {
      case 'online':
        return (
          <Space>
            <VideoCameraOutlined style={{ color: '#1890ff' }} /> {t('online')}
          </Space>
        );
      case 'onsite':
        return (
          <Space>
            <GlobalOutlined style={{ color: '#52c41a' }} /> {t('onsite')}
          </Space>
        );
      case 'athome':
        return (
          <Space>
            <HomeOutlined style={{ color: '#fa8c16' }} /> {t('athome')}
          </Space>
        );
      default:
        return (
          <Space>
            <QuestionOutlined style={{ color: '#d9d9d9' }} /> -
          </Space>
        );
    }
  };

  // Format date only
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    try {
      const date = moment(dateString);
      if (date.isValid()) {
        return date.format('DD MMM YY');
      }
      return '-';
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  };

  // Format time only
  const formatTime = (timeString) => {
    if (!timeString) return '-';
    
    try {
      const time = moment(timeString, 'HH:mm:ss');
      if (time.isValid()) {
        return time.format('HH:mm');
      }
      return timeString;
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  };

  // Format duration to "2:30hrs" format
  const formatDuration = (durationString) => {
    if (!durationString) return '-';
    
    try {
      // Handle format like "2h30m" or "2 30"
      if (durationString.includes('h') && durationString.includes('m')) {
        const hours = parseInt(durationString.split('h')[0]);
        const minutes = parseInt(durationString.split('h')[1].replace('m', ''));
        return `${hours}:${minutes.toString().padStart(2, '0')} hr(s)`;
      }
      
      // Handle format like "2 30" (hours and minutes with space)
      if (durationString.includes(' ')) {
        const [hours, minutes] = durationString.split(' ').map(part => parseInt(part));
        return `${hours}:${minutes.toString().padStart(2, '0')} hr(s)`;
      }
      
      // Try to convert minutes to hours:minutes format
      const totalMinutes = parseInt(durationString);
      if (!isNaN(totalMinutes)) {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours}:${minutes.toString().padStart(2, '0')} hr(s)`;
      }
      
      // If we can't parse it, return as is
      return durationString;
    } catch (error) {
      console.error('Error formatting duration:', error);
      return durationString || '-';
    }
  };

  // Format change log timestamp
  const formatLogTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    try {
      return moment(timestamp).format('DD MMM YY HH:mm');
    } catch (error) {
      return timestamp;
    }
  };

  // Filter class change logs for current class
  const getFilteredClassChangeLogs = () => {
    if (!classChangeLogs || !classData?.classid) return [];
    return classChangeLogs.filter(log => log.classid === classData.classid);
  };

  // Get change type icon
  const getChangeIcon = (changeType) => {
    switch(changeType?.toLowerCase()) {
      case 'reschedule':
      case 'time_change':
        return <ClockCircleOutlined style={{ color: '#fa8c16' }} />;
      case 'teacher_change':
        return <UserOutlined style={{ color: '#1890ff' }} />;
      case 'platform_change':
        return <SwapOutlined style={{ color: '#52c41a' }} />;
      case 'status_change':
        return <EditOutlined style={{ color: '#722ed1' }} />;
      default:
        return <EditOutlined style={{ color: '#8c8c8c' }} />;
    }
  };

  // Get change description based on the log data
  const getChangeDescription = (log) => {
    if (log.changereason) return log.changereason;
    if (log.note) return log.note;
    
    // Generate description based on what changed
    const changes = [];
    if (log.originaldate !== log.newdate) {
      changes.push(`Date changed from ${formatDate(log.originaldate)} to ${formatDate(log.newdate)}`);
    }
    if (log.originalstarttime !== log.newstarttime) {
      changes.push(`Start time changed from ${formatTime(log.originalstarttime)} to ${formatTime(log.newstarttime)}`);
    }
    if (log.originalendtime !== log.newendtime) {
      changes.push(`End time changed from ${formatTime(log.originalendtime)} to ${formatTime(log.newendtime)}`);
    }
    
    return changes.length > 0 ? changes.join(', ') : 'Class details updated';
  };

  const InfoCard = ({ icon, title, value, color = '#1890ff' }) => (
    <Card 
      size="small" 
      style={{ 
        height: '100%',
        borderLeft: `4px solid ${color}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}
    >
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Space>
          {React.cloneElement(icon, { style: { color, fontSize: '16px' } })}
          <Text strong style={{ color: '#595959' }}>{title}</Text>
        </Space>
        <div style={{ fontSize: '16px', fontWeight: '500', color: '#262626' }}>
          {value}
        </div>
      </Space>
    </Card>
  );

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      width={900}
      footer={null}
      centered
      style={{ top: 20 }}
    >
      {classData ? (
        <div>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <Title level={2} style={{ marginBottom: '8px', color: '#1890ff' }}>
              {classData.subject_name}
            </Title>
            <Space size="large">
              {getStatusTag(classData.status)}
              <Text type="secondary" style={{ fontSize: '14px' }}>
                Class ID: {classData.classid}
              </Text>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                Code: {classData.class_code || '-'}
              </Text>
            </Space>
          </div>

          {/* Class Details Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} md={8}>
              <InfoCard
                icon={<UserOutlined />}
                title="TEACHER"
                value={getTeacherName()}
                color="#722ed1"
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoCard
                icon={<CalendarOutlined />}
                title="DATE"
                value={formatDate(classData.date)}
                color="#52c41a"
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoCard
                icon={<ClockCircleOutlined />}
                title="TIME"
                value={`${formatTime(classData.schedule_time)} - ${formatTime(classData.end_time)}`}
                color="#fa8c16"
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoCard
                icon={<BookOutlined />}
                title="DURATION"
                value={formatDuration(classData.schedule_hour || classData.duration)}
                color="#13c2c2"
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoCard
                icon={<VideoCameraOutlined />}
                title="PLATFORM"
                value={formatPlatform(classData.platform)}
                color="#1890ff"
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoCard
                icon={<EnvironmentOutlined />}
                title="CLASSROOM"
                value={classData.classroom_number || '-'}
                color="#f5222d"
              />
            </Col>
          </Row>
          {classData.book_details_materials && (
            <Card
              size="small"
              style={{
                marginBottom: '24px',
                backgroundColor: '#fffbe6',
                borderLeft: '4px solid #faad14',
              }}
            >
              <Space direction="vertical" size="small">
                <Text strong style={{ fontSize: '14px' }}>
                  <EditOutlined style={{ marginRight: 8 }} />
                  Note
                </Text>
                <Text style={{ whiteSpace: 'pre-line' }}>{classData.book_details_materials}</Text>
              </Space>
            </Card>
          )}


          {/* Change Logs Section */}
          <Divider orientation="left">
            <Space>
              <HistoryOutlined style={{ color: '#1890ff' }} />
              <Text strong>Class Change History</Text>
            </Space>
          </Divider>

          <Card 
            style={{ 
              maxHeight: '300px', 
              overflowY: 'auto',
              backgroundColor: '#fafafa'
            }}
          >
            {(() => {
              const filteredLogs = getFilteredClassChangeLogs();
              return filteredLogs && filteredLogs.length > 0 ? (
                <Timeline
                  items={filteredLogs.map((log, index) => ({
                    dot: getChangeIcon('reschedule'), // You can enhance this based on log type
                    children: (
                      <div key={index}>
                        <div style={{ marginBottom: '4px' }}>
                          <Text strong style={{ color: '#262626' }}>
                            {log.note}
                          </Text>
                          <Text type="secondary" style={{ marginLeft: '8px', fontSize: '12px' }}>
                            {formatLogTimestamp(log.updated_at)}
                          </Text>
                        </div>
                        <div style={{ marginBottom: '4px' }}>
                          <Text style={{ fontSize: '13px' }}>
                            {getChangeDescription(log)}
                          </Text>
                        </div>
                        {log.updateby && (
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            Changed by: {log.updateby}
                          </Text>
                        )}
                        {log.originaldate && log.newdate && log.originaldate !== log.newdate && (
                          <div style={{ marginTop: '4px', fontSize: '12px' }}>
                            <Text type="secondary">Date: </Text>
                            <Text delete>{formatDate(log.originaldate)}</Text>
                            <Text type="secondary"> → </Text>
                            <Text mark>{formatDate(log.newdate)}</Text>
                          </div>
                        )}
                        {log.originalstarttime && log.newstarttime && log.originalstarttime !== log.newstarttime && (
                          <div style={{ marginTop: '2px', fontSize: '12px' }}>
                            <Text type="secondary">Time: </Text>
                            <Text delete>{formatTime(log.originalstarttime)} - {formatTime(log.originalendtime)}</Text>
                            <Text type="secondary"> → </Text>
                            <Text mark>{formatTime(log.newstarttime)} - {formatTime(log.newendtime)}</Text>
                          </div>
                        )}
                      </div>
                    ),
                  }))}
                />
              ) : (
                <Empty 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No change history available"
                  style={{ margin: '20px 0' }}
                />
              );
            })()}
          </Card>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Empty description="No class details available" />
        </div>
      )}
    </Modal>
  );
};

export default ClassDetailsModal;